/**
 * "Check by ear" bar of the Song & Timing workspace (W2-08 timing; DESIGN.md §4.15 bottom row, §5.2 "First beat"
 * step 3): Play with clicks, the question "Does the click land on the beat?" and the answers Too early / Just right
 * / Too late, each moving beat 0 by a shrinking step (10, 5, 2 ms, then 1 ms; Just right confirms the timing), the
 * reminder that adjusting by ear does not depend on the headphones, and the audio latency with Calibrate.
 * `ByEarAnswers` is shared with the wizard Beat step.
 */

import type { AppServices } from '../../app/registry/types';
import { Button, Icon, Tooltip } from '../../ui';
import { m } from './timing.messages';
import { answerByEar, openCalibration, togglePlayWithClicks } from './timingActions';
import { useSettingsValue, useTransport, type TimingText } from './timingHooks';
import { byEarStepMs, type ByEarAnswer } from './timingModel';
import { timingSessionFor, useTimingSession, type TimingSessionState } from './timingSession';
import s from './Timing.module.css';

export interface ByEarAnswersProps {
  readonly text: TimingText;
  /** Step of the next answer (ms). */
  readonly stepMs: number;
  onAnswer(a: ByEarAnswer): void;
  onReset(): void;
  /** Show the reset link (after the first answer). */
  readonly canReset: boolean;
  readonly disabledReason?: string | null;
  /** The timing is confirmed: "Just right" shows as chosen (neutral until then, UX-075). */
  readonly confirmed?: boolean;
}

export function ByEarAnswers(props: ByEarAnswersProps) {
  const { text, stepMs } = props;
  const reason = props.disabledReason ?? null;
  const disabled = reason === null ? null : { key: reason };
  return (
    <div className={s.row}>
      <span className={s.question}>{text.t(m.byEarQuestion)}</span>
      <div className={s.answers} role="group" aria-label={text.t(m.byEarQuestion)}>
        <Tooltip content={text.t(m.tooEarlyTip, { ms: stepMs })}>
          <Button
            icon="ChevronLeft"
            disabledReason={disabled}
            onClick={() => {
              props.onAnswer('tooEarly');
            }}
            data-testid="timing-too-early"
          >
            {text.t(m.tooEarly)}
          </Button>
        </Tooltip>
        <Tooltip content={text.t(m.justRightTip)}>
          <Button
            icon="Check"
            variant={props.confirmed === true ? 'primary' : 'secondary'}
            aria-pressed={props.confirmed === true}
            disabledReason={disabled}
            onClick={() => {
              props.onAnswer('justRight');
            }}
            data-testid="timing-just-right"
          >
            {text.t(m.justRight)}
          </Button>
        </Tooltip>
        <Tooltip content={text.t(m.tooLateTip, { ms: stepMs })}>
          <Button
            icon="ChevronRight"
            disabledReason={disabled}
            onClick={() => {
              props.onAnswer('tooLate');
            }}
            data-testid="timing-too-late"
          >
            {text.t(m.tooLate)}
          </Button>
        </Tooltip>
      </div>
      <span className={s.subtle} data-testid="timing-step">
        {text.t(m.byEarStep, { ms: stepMs })}
      </span>
      {props.canReset && (
        <Button
          size="sm"
          variant="ghost"
          icon="RotateCcw"
          onClick={() => {
            props.onReset();
          }}
        >
          {text.t(m.byEarReset)}
        </Button>
      )}
    </div>
  );
}

export interface OffsetHelperProps {
  readonly services: AppServices;
  readonly text: TimingText;
  readonly confirmed: boolean;
  readonly hasSong: boolean;
}

const selectIndex = (st: TimingSessionState) => st.byEarIndex;
const selectClicks = (st: TimingSessionState) => st.clicks;

export function OffsetHelper(props: OffsetHelperProps) {
  const { services, text } = props;
  const index = useTimingSession(services, selectIndex);
  const clicks = useTimingSession(services, selectClicks);
  const transport = useTransport(services);
  const settings = useSettingsValue(services);
  const calibration = settings.audio.latency.calibrationMs;
  const playing = clicks && transport.playing;
  return (
    <div className={s.bottom} data-region="timing" aria-label={text.t(m.panelBottom)} role="region">
      <Button
        icon={playing ? 'Square' : 'Play'}
        variant={playing ? 'secondary' : 'primary'}
        command="timing.playWithClicks"
        onClick={() => {
          void togglePlayWithClicks(services);
        }}
        disabledReason={props.hasSong ? null : { key: m.noSongReason }}
        data-testid="timing-play-clicks"
      >
        {text.t(playing ? m.stopClicks : m.playWithClicks)}
      </Button>
      <ByEarAnswers
        text={text}
        stepMs={byEarStepMs(index)}
        canReset={index > 0}
        confirmed={props.confirmed}
        disabledReason={props.hasSong ? null : m.noSongReason}
        onAnswer={(a) => {
          answerByEar(services, a);
        }}
        onReset={() => {
          timingSessionFor(services).set({ byEarIndex: 0 });
        }}
      />
      <span className={s.row}>
        <Icon name={props.confirmed ? 'CircleCheck' : 'CircleAlert'} size={14} />
        <span className={s.muted}>{text.t(props.confirmed ? m.confirmedYes : m.confirmedNo)}</span>
      </span>
      <span className={s.divider} aria-hidden="true" />
      <span className={s.row}>
        <Icon name="Headphones" size={14} />
        <span className={s.help}>{text.t(m.byEarHeadphones)}</span>
      </span>
      <span className={s.row}>
        <span className={s.muted}>{text.t(m.latencyLabel)}</span>
        <span className={s.mono}>
          {calibration === 0 ? text.t(m.latencyNone) : text.t(m.latencyValue, { ms: calibration })}
        </span>
        <Tooltip content={text.t(m.calibrateTip)}>
          <Button
            size="sm"
            icon="Timer"
            onClick={() => {
              void openCalibration(services);
            }}
          >
            {text.t(m.calibrate)}
          </Button>
        </Tooltip>
      </span>
    </div>
  );
}
